import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { PROFILES } from '../profiles/index';
import { downloadTemplate } from '../lib/templates';
import { checkColumns } from '../lib/column-checker';
import { processFile } from '../lib/transformer';
import { buildPlan, setColumnMapping, rebuildPlan } from '../lib/ingest';
import MappingReview from './MappingReview';

// Payroll (iSolved) has its own multi-sheet parser; it skips the mapping UI.
const USES_MAPPING = (profile) => profile !== 'gl8610xl-payroll';

export default function PrepareScreen({ profile, onFileProcessed, onBack }) {
  const profileDef = PROFILES[profile];
  const [checkedItems, setCheckedItems] = useState({});
  const [file, setFile] = useState(null);
  const [columnCheck, setColumnCheck] = useState(null);
  const [plan, setPlan] = useState(null);
  const [checking, setChecking] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();
  const wbRef = useRef(null);

  const checklist = profileDef.prepareChecklist ?? [];
  const allChecked = checklist.every((_, i) => checkedItems[i]);

  const toggleCheck = (i) => {
    setCheckedItems((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const handleFile = useCallback(async (f) => {
    setFile(f);
    setError(null);
    setColumnCheck(null);
    setPlan(null);
    wbRef.current = null;
    if (!f) return;

    setChecking(true);
    try {
      if (USES_MAPPING(profile)) {
        const buffer = await f.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
        wbRef.current = wb;
        setPlan(buildPlan(wb, profile));
      } else {
        setColumnCheck(await checkColumns(f, profile));
      }
    } catch (err) {
      setError(`Could not read file: ${err.message}`);
    } finally {
      setChecking(false);
    }
  }, [profile]);

  const handleMapColumn = useCallback((columnIndex, canonical) => {
    setPlan((prev) => (prev ? setColumnMapping(wbRef.current, prev, columnIndex, canonical) : prev));
  }, []);

  const handleChangeSheet = useCallback((sheetName) => {
    setPlan((prev) => (prev ? rebuildPlan(wbRef.current, prev, { sheetName }) : prev));
  }, []);

  const handleChangeHeaderRow = useCallback((headerRowIndex) => {
    setPlan((prev) => (prev ? rebuildPlan(wbRef.current, prev, { headerRowIndex }) : prev));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && /\.(xlsx|xls|csv)$/i.test(f.name)) {
      handleFile(f);
    } else {
      setError('Please upload an .xlsx, .xls, or .csv file.');
    }
  }, [handleFile]);

  const handleFileSelect = useCallback((e) => {
    const f = e.target.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleProcess = useCallback(async () => {
    if (!file) return;
    setProcessing(true);
    setError(null);
    try {
      const result = await processFile(file, profile, USES_MAPPING(profile) ? plan : null);
      onFileProcessed(result);
    } catch (err) {
      setError(`Processing error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  }, [file, profile, plan, onFileProcessed]);

  useEffect(() => {
    setCheckedItems({});
    setFile(null);
    setColumnCheck(null);
    setPlan(null);
    wbRef.current = null;
    setError(null);
  }, [profile]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            onClick={onBack}
            className="text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            ← Change import type
          </button>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <span>{profileDef.icon}</span>
            {profileDef.name}
            <span className="text-xs font-mono text-gray-400 font-normal">{profileDef.interfaceId}</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">{profileDef.bestFor}</p>
        </div>
        <button
          onClick={() => downloadTemplate(profile)}
          className="shrink-0 px-4 py-2 text-sm font-medium text-mv-700 bg-mv-50 border border-mv-200 rounded-lg hover:bg-mv-100 transition-colors"
        >
          ↓ Download Template
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Checklist */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-semibold text-sm mb-3">Preparation Checklist</h3>
          <ul className="space-y-2">
            {checklist.map((item, i) => (
              <li key={i}>
                <label className="flex items-start gap-2 text-sm cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!checkedItems[i]}
                    onChange={() => toggleCheck(i)}
                    className="mt-0.5 rounded border-gray-300 text-mv-600 focus:ring-mv-500"
                  />
                  <span className={checkedItems[i] ? 'text-gray-400 line-through' : 'text-gray-700'}>
                    {item}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        {/* Column guide */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-semibold text-sm mb-3">Source File Columns</h3>
          <div className="space-y-2">
            {profileDef.columnGuide.map((col) => (
              <div key={col.column} className="flex items-start gap-2 text-sm">
                <code className="shrink-0 px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-700">
                  {col.column}
                </code>
                <div>
                  <span className="text-gray-600">{col.description}</span>
                  {col.required && (
                    <span className="ml-1 text-xs text-red-500 font-medium">required</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key rules */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-semibold text-sm mb-3">Key Rules</h3>
          <dl className="space-y-3">
            {profileDef.keyRules.map((rule) => (
              <div key={rule.label}>
                <dt className="text-xs font-semibold text-mv-700">{rule.label}</dt>
                <dd className="text-xs text-gray-600 mt-0.5">{rule.detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Output preview */}
      <div className="bg-gray-900 rounded-lg p-5 overflow-x-auto">
        <h3 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">
          MV Output Format Preview
        </h3>
        <pre className="text-xs text-green-400 font-mono whitespace-pre leading-relaxed">
          {profileDef.outputPreview}
        </pre>
      </div>

      {/* Upload zone */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <h3 className="font-semibold text-sm">Upload Your Source File</h3>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            dragOver
              ? 'border-mv-500 bg-mv-50'
              : file
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          {file ? (
            <div>
              <div className="text-2xl mb-1">📄</div>
              <p className="font-medium text-green-700">{file.name}</p>
              <p className="text-sm text-gray-500 mt-1">
                {(file.size / 1024).toFixed(1)} KB — Click or drop to replace
              </p>
            </div>
          ) : (
            <div>
              <div className="text-2xl mb-1">📂</div>
              <p className="font-medium text-gray-600">Drop your prepared Excel file here</p>
              <p className="text-sm text-gray-400 mt-1">or click to browse</p>
            </div>
          )}
        </div>

        {/* Column match status */}
        {checking && (
          <p className="text-sm text-gray-500">Reading file…</p>
        )}

        {/* Mapping review (budget & JE) */}
        {plan && !checking && (
          <MappingReview
            plan={plan}
            onMapColumn={handleMapColumn}
            onChangeSheet={handleChangeSheet}
            onChangeHeaderRow={handleChangeHeaderRow}
          />
        )}

        {columnCheck && !checking && (
          <div className={`rounded-lg p-4 text-sm ${
            columnCheck.isReady ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <p className="font-medium">
              {columnCheck.isReady
                ? `✓ File looks good — ${columnCheck.rowCount} data rows found`
                : '⚠ Column issues detected — you can still try to process, but fix these first'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {columnCheck.found.map((col) => (
                <span key={col} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-mono">
                  ✓ {col}
                </span>
              ))}
              {columnCheck.requiredMissing.map((col) => (
                <span key={col} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-mono">
                  ✕ missing: {col}
                </span>
              ))}
            </div>
            {columnCheck.extra.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                Extra columns (ignored): {columnCheck.extra.join(', ')}
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          {(() => {
            const blockedByMapping =
              USES_MAPPING(profile) && (!plan || plan.unmappedRequired.length > 0);
            const disabled = !file || processing || blockedByMapping;
            return (
              <button
                onClick={handleProcess}
                disabled={disabled}
                title={blockedByMapping && plan ? `Map required field(s): ${plan.unmappedRequired.join(', ')}` : undefined}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
                  disabled
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-mv-600 text-white hover:bg-mv-700 shadow-md'
                }`}
              >
                {processing ? 'Processing…' : 'Validate & Transform →'}
              </button>
            );
          })()}
        </div>

        {!allChecked && file && (
          <p className="text-xs text-gray-400 text-right">
            Tip: complete the checklist above to avoid common import errors
          </p>
        )}
      </div>
    </div>
  );
}
